import { useState, useReducer, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AIPromptBar from './components/AIPromptBar';
import ScreenManager from './components/ScreenManager';
import ComponentLibraryPanel, { ComponentItem, componentLibrary } from './components/ComponentLibrary';
import PhoneCanvas, { CanvasElement } from './components/PhoneCanvas';
import PropertiesPanel from './components/PropertiesPanel';
import PublishModal from './components/PublishModal';
import PluginManager from './components/PluginManager';
import { templates } from '../../mocks/templates';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Screen {
  id: string;
  name: string;
  elements: CanvasElement[];
}

interface AppSettings {
  primaryColor: string;
  backgroundColor: string;
  fontFamily: string;
}

interface BuilderState {
  screens: Screen[];
  activeScreenId: string;
  selectedElementId: string | null;
  appName: string;
  appSettings: AppSettings;
}

type Action =
  | { type: 'ADD_ELEMENT'; element: CanvasElement }
  | { type: 'UPDATE_ELEMENT'; id: string; props: Record<string, unknown> }
  | { type: 'DELETE_ELEMENT'; id: string }
  | { type: 'SELECT_ELEMENT'; id: string | null }
  | { type: 'REORDER_ELEMENTS'; from: number; to: number }
  | { type: 'SET_SCREEN_ELEMENTS'; elements: CanvasElement[] }
  | { type: 'ADD_SCREEN'; screen: Screen }
  | { type: 'DELETE_SCREEN'; id: string }
  | { type: 'RENAME_SCREEN'; id: string; name: string }
  | { type: 'SET_ACTIVE_SCREEN'; id: string }
  | { type: 'UPDATE_APP_NAME'; name: string }
  | { type: 'UPDATE_APP_SETTINGS'; settings: Partial<AppSettings> }
  | { type: 'RESTORE'; state: BuilderState };

const defaultScreen: Screen = { id: 'screen-1', name: 'Home', elements: [] };

const initialState: BuilderState = {
  screens: [defaultScreen],
  activeScreenId: 'screen-1',
  selectedElementId: null,
  appName: 'My App',
  appSettings: { primaryColor: '#14B8A6', backgroundColor: '#FFFFFF', fontFamily: 'Inter' },
};

function getActiveScreen(state: BuilderState): Screen {
  return state.screens.find((s) => s.id === state.activeScreenId) ?? state.screens[0];
}

function reducer(state: BuilderState, action: Action): BuilderState {
  const updateActiveScreen = (updater: (s: Screen) => Screen): BuilderState => ({
    ...state,
    screens: state.screens.map((s) => (s.id === state.activeScreenId ? updater(s) : s)),
  });

  switch (action.type) {
    case 'ADD_ELEMENT':
      return {
        ...updateActiveScreen((s) => ({ ...s, elements: [...s.elements, action.element] })),
        selectedElementId: action.element.id,
      };
    case 'UPDATE_ELEMENT':
      return updateActiveScreen((s) => ({
        ...s,
        elements: s.elements.map((el) =>
          el.id === action.id ? { ...el, properties: { ...el.properties, ...action.props } } : el
        ),
      }));
    case 'DELETE_ELEMENT':
      return {
        ...updateActiveScreen((s) => ({ ...s, elements: s.elements.filter((el) => el.id !== action.id) })),
        selectedElementId: state.selectedElementId === action.id ? null : state.selectedElementId,
      };
    case 'SELECT_ELEMENT':
      return { ...state, selectedElementId: action.id };
    case 'REORDER_ELEMENTS':
      return updateActiveScreen((s) => {
        const els = [...s.elements];
        const [moved] = els.splice(action.from, 1);
        els.splice(action.to, 0, moved);
        return { ...s, elements: els };
      });
    case 'SET_SCREEN_ELEMENTS':
      return updateActiveScreen((s) => ({ ...s, elements: action.elements }));
    case 'ADD_SCREEN':
      return { ...state, screens: [...state.screens, action.screen], activeScreenId: action.screen.id, selectedElementId: null };
    case 'DELETE_SCREEN': {
      const remaining = state.screens.filter((s) => s.id !== action.id);
      return {
        ...state,
        screens: remaining,
        activeScreenId: remaining[0]?.id ?? '',
        selectedElementId: null,
      };
    }
    case 'RENAME_SCREEN':
      return { ...state, screens: state.screens.map((s) => (s.id === action.id ? { ...s, name: action.name } : s)) };
    case 'SET_ACTIVE_SCREEN':
      return { ...state, activeScreenId: action.id, selectedElementId: null };
    case 'UPDATE_APP_NAME':
      return { ...state, appName: action.name };
    case 'UPDATE_APP_SETTINGS':
      return { ...state, appSettings: { ...state.appSettings, ...action.settings } };
    case 'RESTORE':
      return action.state;
    default:
      return state;
  }
}

// ─── AI Generator ─────────────────────────────────────────────────────────────
function generateAppFromPrompt(prompt: string): { screens: Screen[]; appName: string } {
  const lower = prompt.toLowerCase();

  const makeEl = (type: string, props: Record<string, unknown>): CanvasElement => ({
    id: `${type}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    properties: { ...(componentLibrary.find((c) => c.id === type)?.defaultProps ?? {}), ...props },
  });

  if (lower.includes('food') || lower.includes('restaurant') || lower.includes('delivery')) {
    return {
      appName: 'FoodieGo',
      screens: [
        {
          id: 'screen-1', name: 'Home', elements: [
            makeEl('header', { title: 'FoodieGo', showMenu: true }),
            makeEl('search', { placeholder: 'Search restaurants...' }),
            makeEl('heading', { content: 'Featured Today', fontSize: 18 }),
            makeEl('carousel', { count: 3 }),
            makeEl('heading', { content: 'Near You', fontSize: 18 }),
            makeEl('productcard', { name: 'Burger Palace', price: '$12.99', originalPrice: '$15.99', badge: 'Popular', rating: 4.8 }),
            makeEl('productcard', { name: 'Pizza Heaven', price: '$9.99', originalPrice: '', badge: 'New', rating: 4.5 }),
            makeEl('tabbar', { tabs: ['Home', 'Search', 'Orders', 'Profile'], activeTab: 0 }),
          ],
        },
        {
          id: 'screen-2', name: 'Cart', elements: [
            makeEl('header', { title: 'Your Cart', showBack: true }),
            makeEl('list', { title: 'Burger Palace Combo', subtitle: 'x1 — $12.99', showArrow: false, iconName: 'ri-shopping-bag-line' }),
            makeEl('list', { title: 'Fries (Large)', subtitle: 'x2 — $5.98', showArrow: false, iconName: 'ri-shopping-bag-line' }),
            makeEl('divider', {}),
            makeEl('text', { content: 'Total: $18.97', fontSize: 18, fontWeight: 'bold', align: 'right' }),
            makeEl('button', { text: 'Place Order', backgroundColor: '#14B8A6' }),
          ],
        },
      ],
    };
  }

  if (lower.includes('fitness') || lower.includes('workout') || lower.includes('gym') || lower.includes('health')) {
    return {
      appName: 'FitTrack',
      screens: [
        {
          id: 'screen-1', name: 'Dashboard', elements: [
            makeEl('header', { title: 'FitTrack', showMenu: true }),
            makeEl('usercard', { name: 'Alex Johnson', role: 'Premium Member', followers: '142 days' }),
            makeEl('heading', { content: "Today's Progress", fontSize: 18 }),
            makeEl('progress', { value: 72, color: '#14B8A6' }),
            makeEl('text', { content: '7,200 / 10,000 steps', fontSize: 13, color: '#6B7280' }),
            makeEl('heading', { content: 'Workouts', fontSize: 18 }),
            makeEl('list', { title: 'Morning Run', subtitle: '5.2 km · 32 min', iconName: 'ri-run-line', iconColor: '#14B8A6' }),
            makeEl('list', { title: 'Strength Training', subtitle: '45 min · 320 cal', iconName: 'ri-heart-pulse-line', iconColor: '#F59E0B' }),
            makeEl('button', { text: 'Start Workout', backgroundColor: '#14B8A6' }),
            makeEl('tabbar', { tabs: ['Home', 'Workouts', 'Stats', 'Profile'], activeTab: 0 }),
          ],
        },
        {
          id: 'screen-2', name: 'Stats', elements: [
            makeEl('header', { title: 'Statistics', showBack: true }),
            makeEl('heading', { content: 'Weekly Summary', fontSize: 18 }),
            makeEl('card', { title: 'Calories Burned', subtitle: '2,840 kcal this week' }),
            makeEl('card', { title: 'Active Minutes', subtitle: '185 min this week' }),
            makeEl('card', { title: 'Workouts Done', subtitle: '5 sessions completed' }),
          ],
        },
      ],
    };
  }

  if (lower.includes('ecommerce') || lower.includes('shop') || lower.includes('store') || lower.includes('clothing')) {
    return {
      appName: 'ShopNow',
      screens: [
        {
          id: 'screen-1', name: 'Shop', elements: [
            makeEl('header', { title: 'ShopNow', showMenu: true }),
            makeEl('search', { placeholder: 'Search products...' }),
            makeEl('carousel', { count: 3 }),
            makeEl('heading', { content: 'New Arrivals', fontSize: 18 }),
            makeEl('productcard', { name: 'Classic White Tee', price: '$24.99', originalPrice: '$39.99', badge: 'Sale', rating: 4.7 }),
            makeEl('productcard', { name: 'Slim Fit Jeans', price: '$59.99', originalPrice: '$79.99', badge: 'Hot', rating: 4.9 }),
            makeEl('tabbar', { tabs: ['Home', 'Search', 'Cart', 'Profile'], activeTab: 0 }),
          ],
        },
        {
          id: 'screen-2', name: 'Product', elements: [
            makeEl('header', { title: 'Product Detail', showBack: true }),
            makeEl('image', { height: 220, borderRadius: 12 }),
            makeEl('heading', { content: 'Classic White Tee', fontSize: 22 }),
            makeEl('rating', { value: 4, max: 5 }),
            makeEl('text', { content: '$24.99', fontSize: 20, color: '#14B8A6', fontWeight: 'bold' }),
            makeEl('text', { content: 'Premium cotton blend, perfect for everyday wear. Available in multiple sizes.', fontSize: 14, color: '#6B7280' }),
            makeEl('button', { text: 'Add to Cart', backgroundColor: '#14B8A6' }),
          ],
        },
      ],
    };
  }

  if (lower.includes('booking') || lower.includes('salon') || lower.includes('appointment') || lower.includes('schedule')) {
    return {
      appName: 'BookEasy',
      screens: [
        {
          id: 'screen-1', name: 'Services', elements: [
            makeEl('header', { title: 'BookEasy', showMenu: true }),
            makeEl('heading', { content: 'Our Services', fontSize: 20 }),
            makeEl('list', { title: 'Haircut & Style', subtitle: '45 min · $45', iconName: 'ri-scissors-line', iconColor: '#14B8A6' }),
            makeEl('list', { title: 'Color Treatment', subtitle: '90 min · $120', iconName: 'ri-palette-line', iconColor: '#F59E0B' }),
            makeEl('list', { title: 'Facial Treatment', subtitle: '60 min · $80', iconName: 'ri-user-smile-line', iconColor: '#EC4899' }),
            makeEl('list', { title: 'Manicure & Pedicure', subtitle: '75 min · $65', iconName: 'ri-hand-heart-line', iconColor: '#8B5CF6' }),
            makeEl('button', { text: 'Book Appointment', backgroundColor: '#14B8A6' }),
            makeEl('tabbar', { tabs: ['Home', 'Book', 'History', 'Profile'], activeTab: 0 }),
          ],
        },
      ],
    };
  }

  if (lower.includes('social') || lower.includes('feed') || lower.includes('community') || lower.includes('photo')) {
    return {
      appName: 'ConnectMe',
      screens: [
        {
          id: 'screen-1', name: 'Feed', elements: [
            makeEl('header', { title: 'ConnectMe', showMenu: true }),
            makeEl('search', { placeholder: 'Search people...' }),
            makeEl('heading', { content: 'Suggested', fontSize: 16 }),
            makeEl('usercard', { name: 'Sarah Williams', role: 'Photographer', followers: '12.4k' }),
            makeEl('usercard', { name: 'Mike Chen', role: 'Designer', followers: '8.1k' }),
            makeEl('heading', { content: 'Latest Posts', fontSize: 16 }),
            makeEl('image', { height: 180, borderRadius: 12 }),
            makeEl('text', { content: 'Beautiful sunset at the beach 🌅', fontSize: 14, color: '#374151' }),
            makeEl('tabbar', { tabs: ['Feed', 'Explore', 'Post', 'Profile'], activeTab: 0 }),
          ],
        },
      ],
    };
  }

  if (lower.includes('task') || lower.includes('todo') || lower.includes('productivity') || lower.includes('manager')) {
    return {
      appName: 'TaskFlow',
      screens: [
        {
          id: 'screen-1', name: 'Tasks', elements: [
            makeEl('header', { title: 'TaskFlow', showMenu: true }),
            makeEl('heading', { content: "Today's Tasks", fontSize: 20 }),
            makeEl('progress', { value: 60, color: '#14B8A6' }),
            makeEl('text', { content: '3 of 5 tasks completed', fontSize: 13, color: '#6B7280' }),
            makeEl('list', { title: 'Design new landing page', subtitle: 'Due today · High priority', iconName: 'ri-checkbox-circle-line', iconColor: '#14B8A6' }),
            makeEl('list', { title: 'Review pull requests', subtitle: 'Due today · Medium', iconName: 'ri-git-pull-request-line', iconColor: '#F59E0B' }),
            makeEl('list', { title: 'Team standup meeting', subtitle: '3:00 PM · Recurring', iconName: 'ri-team-line', iconColor: '#8B5CF6' }),
            makeEl('button', { text: '+ Add New Task', backgroundColor: '#14B8A6' }),
            makeEl('tabbar', { tabs: ['Tasks', 'Projects', 'Calendar', 'Profile'], activeTab: 0 }),
          ],
        },
      ],
    };
  }

  // Generic fallback
  return {
    appName: prompt.split(' ').slice(0, 2).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('') + ' App',
    screens: [
      {
        id: 'screen-1', name: 'Home', elements: [
          makeEl('header', { title: 'My App', showMenu: true }),
          makeEl('heading', { content: 'Welcome!', fontSize: 24 }),
          makeEl('text', { content: prompt, fontSize: 14, color: '#6B7280' }),
          makeEl('image', { height: 160, borderRadius: 12 }),
          makeEl('button', { text: 'Get Started', backgroundColor: '#14B8A6' }),
          makeEl('tabbar', { tabs: ['Home', 'Explore', 'Profile'], activeTab: 0 }),
        ],
      },
    ],
  };
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BuilderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [history, setHistory] = useState<BuilderState[]>([initialState]);
  const [histIdx, setHistIdx] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPlugins, setShowPlugins] = useState(false);
  const [device, setDevice] = useState<'ios' | 'android'>('ios');
  const [leftTab, setLeftTab] = useState<'components' | 'layers' | 'templates'>('components');
  const [zoom, setZoom] = useState(100);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load template or saved app on mount
  useEffect(() => {
    if (isInitialized) return;
    
    // Check if loading a saved app from dashboard
    const loadAppData = sessionStorage.getItem('loadApp');
    if (loadAppData) {
      try {
        const appState = JSON.parse(loadAppData);
        dispatch({ type: 'RESTORE', state: appState });
        sessionStorage.removeItem('loadApp');
        setIsInitialized(true);
        return;
      } catch (e) {
        console.error('Failed to load app:', e);
      }
    }

    // Check if loading from template
    const templateId = searchParams.get('template');
    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        // Generate app from template
        const result = generateAppFromPrompt(template.name + ' ' + template.description);
        const newState: BuilderState = {
          ...state,
          appName: template.name,
          screens: result.screens,
          activeScreenId: result.screens[0].id,
          selectedElementId: null,
        };
        dispatch({ type: 'RESTORE', state: newState });
      }
    }
    
    setIsInitialized(true);
  }, [searchParams, isInitialized]);

  const activeScreen = getActiveScreen(state);
  const selectedElement = activeScreen.elements.find((el) => el.id === state.selectedElementId) ?? null;

  // Listen for delete events from canvas
  useEffect(() => {
    const handler = (e: Event) => {
      const { id } = (e as CustomEvent).detail;
      pushHistory({ ...state });
      dispatch({ type: 'DELETE_ELEMENT', id });
    };
    window.addEventListener('deleteElement', handler);
    return () => window.removeEventListener('deleteElement', handler);
  }, [state]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || (target as HTMLElement).isContentEditable) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && state.selectedElementId) {
        e.preventDefault();
        pushHistory(state);
        dispatch({ type: 'DELETE_ELEMENT', id: state.selectedElementId });
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') { e.preventDefault(); handleUndo(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state, histIdx, history]);

  const pushHistory = useCallback((s: BuilderState) => {
    const next = history.slice(0, histIdx + 1);
    next.push(s);
    if (next.length > 40) next.shift();
    setHistory(next);
    setHistIdx(Math.min(histIdx + 1, next.length - 1));
  }, [history, histIdx]);

  const handleUndo = () => {
    if (histIdx > 0) {
      const idx = histIdx - 1;
      setHistIdx(idx);
      dispatch({ type: 'RESTORE', state: history[idx] });
    }
  };

  const handleRedo = () => {
    if (histIdx < history.length - 1) {
      const idx = histIdx + 1;
      setHistIdx(idx);
      dispatch({ type: 'RESTORE', state: history[idx] });
    }
  };

  const handleAddComponent = (comp: ComponentItem) => {
    const el: CanvasElement = {
      id: `${comp.id}-${Date.now()}`,
      type: comp.id,
      properties: { ...comp.defaultProps },
    };
    pushHistory(state);
    dispatch({ type: 'ADD_ELEMENT', element: el });
  };

  const handleDragStart = (e: React.DragEvent, comp: ComponentItem) => {
    e.dataTransfer.setData('component', JSON.stringify(comp));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('component');
    if (!data) return;
    const comp = JSON.parse(data) as ComponentItem;
    const el: CanvasElement = {
      id: `${comp.id}-${Date.now()}`,
      type: comp.id,
      properties: { ...comp.defaultProps },
    };
    pushHistory(state);
    dispatch({ type: 'ADD_ELEMENT', element: el });
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleGenerate = (prompt: string) => {
    setIsGenerating(true);
    setTimeout(() => {
      const result = generateAppFromPrompt(prompt);
      const newState: BuilderState = {
        ...state,
        appName: result.appName,
        screens: result.screens,
        activeScreenId: result.screens[0].id,
        selectedElementId: null,
      };
      pushHistory(state);
      dispatch({ type: 'RESTORE', state: newState });
      setIsGenerating(false);
    }, 1800);
  };

  const handleSave = () => {
    // Save to localStorage for dashboard to retrieve
    const savedApps = JSON.parse(localStorage.getItem('myApps') || '[]');
    const appData = {
      id: Date.now().toString(),
      name: state.appName,
      screens: state.screens.length,
      lastEdited: 'Just now',
      published: false,
      thumbnail: 'https://public.readdy.ai/ai/img_res/948388f2f1b42443c37c84e603c25491.jpg' + Date.now() + '&orientation=portrait',
      data: state
    };
    
    // Check if app already exists (by name) and update, otherwise add new
    const existingIndex = savedApps.findIndex((app: any) => app.name === state.appName);
    if (existingIndex >= 0) {
      savedApps[existingIndex] = { ...savedApps[existingIndex], ...appData, id: savedApps[existingIndex].id };
    } else {
      savedApps.push(appData);
    }
    
    localStorage.setItem('myApps', JSON.stringify(savedApps));

    const toast = document.createElement('div');
    toast.className = 'fixed top-6 right-6 bg-teal-500 text-white px-5 py-3 rounded-xl shadow-xl z-[200] flex items-center gap-2 text-sm font-semibold animate-[fadeInDown_0.3s_ease]';
    toast.innerHTML = '<i class="ri-check-line text-lg"></i> Project saved!';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
  };

  return (
    <div className="h-screen w-screen bg-gray-50 flex flex-col overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── Top Toolbar ── */}
      <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0 gap-3">
        {/* Left */}
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/dashboard')} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer flex-shrink-0">
            <i className="ri-arrow-left-line text-lg text-gray-700"></i>
          </button>
          <div className="h-6 w-px bg-gray-200 flex-shrink-0"></div>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex-shrink-0">
              <i className="ri-smartphone-line text-white text-sm"></i>
            </div>
            <input
              type="text"
              value={state.appName}
              onChange={(e) => dispatch({ type: 'UPDATE_APP_NAME', name: e.target.value })}
              className="text-sm font-bold text-gray-800 bg-transparent border-none outline-none focus:bg-gray-50 px-2 py-1 rounded-lg cursor-text min-w-0 max-w-[160px]"
            />
          </div>
        </div>

        {/* Center — AI Prompt */}
        <div className="flex-1 max-w-xl">
          <AIPromptBar onGenerate={handleGenerate} isGenerating={isGenerating} />
        </div>

        {/* Right */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={handleUndo} disabled={histIdx === 0}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${histIdx === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100 cursor-pointer'}`}>
            <i className="ri-arrow-go-back-line text-base"></i>
          </button>
          <button onClick={handleRedo} disabled={histIdx === history.length - 1}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${histIdx === history.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100 cursor-pointer'}`}>
            <i className="ri-arrow-go-forward-line text-base"></i>
          </button>
          <div className="h-6 w-px bg-gray-200 mx-1"></div>
          <button
            onClick={() => setShowPlugins(true)}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-plug-line text-sm"></i>Plugins
          </button>
          <button onClick={() => setShowPreview(true)}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
            <i className="ri-eye-line text-sm"></i>Preview
          </button>
          <button onClick={handleSave}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
            <i className="ri-save-line text-sm"></i>Save
          </button>
          <button onClick={() => setShowPublish(true)}
            className="px-4 py-1.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs font-bold rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center gap-1.5 shadow-md shadow-teal-500/20 whitespace-nowrap cursor-pointer">
            <i className="ri-rocket-line text-sm"></i>Publish
          </button>
        </div>
      </div>

      {/* ── Screen Manager ── */}
      <ScreenManager
        screens={state.screens}
        activeScreenId={state.activeScreenId}
        onSelectScreen={(id) => dispatch({ type: 'SET_ACTIVE_SCREEN', id })}
        onAddScreen={() => {
          const id = `screen-${Date.now()}`;
          dispatch({ type: 'ADD_SCREEN', screen: { id, name: `Screen ${state.screens.length + 1}`, elements: [] } });
        }}
        onDeleteScreen={(id) => dispatch({ type: 'DELETE_SCREEN', id })}
        onRenameScreen={(id, name) => dispatch({ type: 'RENAME_SCREEN', id, name })}
      />

      {/* ── Main 3-Panel Layout ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* Left Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-2 border-b border-gray-100">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-full">
              <button onClick={() => setLeftTab('components')}
                className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${leftTab === 'components' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                <i className="ri-apps-line mr-1"></i>Components
              </button>
              <button onClick={() => setLeftTab('layers')}
                className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${leftTab === 'layers' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                <i className="ri-stack-line mr-1"></i>Layers
              </button>
              <button onClick={() => setLeftTab('templates')}
                className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${leftTab === 'templates' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                <i className="ri-layout-grid-line mr-1"></i>Templates
              </button>
            </div>
          </div>

          {leftTab === 'components' ? (
            <ComponentLibraryPanel onDragStart={handleDragStart} onAddComponent={handleAddComponent} />
          ) : leftTab === 'templates' ? (
            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-xs font-bold text-gray-700 mb-3">Quick Templates</p>
              <div className="space-y-2">
                {[
                  { name: 'Food Delivery', prompt: 'food delivery app' },
                  { name: 'Fitness Tracker', prompt: 'fitness workout tracker' },
                  { name: 'eCommerce Shop', prompt: 'ecommerce clothing store' },
                  { name: 'Booking App', prompt: 'salon booking appointment' },
                  { name: 'Social Feed', prompt: 'social media feed' },
                  { name: 'Task Manager', prompt: 'task todo productivity' },
                ].map((template) => (
                  <button
                    key={template.name}
                    onClick={() => handleGenerate(template.prompt)}
                    className="w-full p-3 bg-gradient-to-br from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 border border-teal-200 rounded-lg text-left transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-smartphone-line text-teal-600"></i>
                      <span className="text-xs font-bold text-gray-900">{template.name}</span>
                    </div>
                    <p className="text-xs text-gray-500">Click to load template</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-3">
              {activeScreen.elements.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <i className="ri-stack-line text-xl text-gray-400"></i>
                  </div>
                  <p className="text-xs text-gray-500 font-medium">No elements yet</p>
                  <p className="text-xs text-gray-400 mt-0.5">Add components to see layers</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {activeScreen.elements.map((el, i) => {
                    const comp = componentLibrary.find((c) => c.id === el.type);
                    return (
                      <div key={el.id}
                        onClick={() => dispatch({ type: 'SELECT_ELEMENT', id: el.id })}
                        className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all ${state.selectedElementId === el.id ? 'bg-teal-50 border border-teal-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                        <i className={`${comp?.icon ?? 'ri-box-line'} text-sm ${state.selectedElementId === el.id ? 'text-teal-600' : 'text-gray-400'}`}></i>
                        <span className={`text-xs font-medium flex-1 truncate ${state.selectedElementId === el.id ? 'text-teal-800' : 'text-gray-700'}`}>
                          {comp?.name ?? el.type} {i + 1}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); pushHistory(state); dispatch({ type: 'DELETE_ELEMENT', id: el.id }); }}
                          className="w-5 h-5 flex items-center justify-center hover:bg-red-100 rounded text-gray-300 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0">
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Center Canvas */}
        <div className="flex-1 bg-gray-100 flex flex-col items-center justify-start overflow-auto relative">
          {/* Device Toggle + Zoom */}
          <div className="sticky top-0 z-10 w-full flex items-center justify-between px-6 py-2 bg-gray-100/90 backdrop-blur-sm border-b border-gray-200">
            <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200 shadow-sm">
              <button onClick={() => setDevice('ios')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1 ${device === 'ios' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                <i className="ri-apple-fill text-sm"></i>iOS
              </button>
              <button onClick={() => setDevice('android')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1 ${device === 'android' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                <i className="ri-android-fill text-sm"></i>Android
              </button>
            </div>
            <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200 shadow-sm">
              <button onClick={() => setZoom(Math.max(60, zoom - 10))} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded text-gray-600 cursor-pointer">
                <i className="ri-subtract-line text-sm"></i>
              </button>
              <span className="text-xs font-semibold text-gray-600 w-10 text-center">{zoom}%</span>
              <button onClick={() => setZoom(Math.min(130, zoom + 10))} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded text-gray-600 cursor-pointer">
                <i className="ri-add-line text-sm"></i>
              </button>
            </div>
          </div>

          {/* Phone */}
          <div className="flex items-start justify-center py-8 px-4">
            <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}>
              <PhoneCanvas
                elements={activeScreen.elements}
                selectedId={state.selectedElementId}
                onSelect={(id) => dispatch({ type: 'SELECT_ELEMENT', id })}
                onDrop={handleCanvasDrop}
                onDragOver={handleCanvasDragOver}
                onReorder={(from, to) => { pushHistory(state); dispatch({ type: 'REORDER_ELEMENTS', from, to }); }}
                appSettings={state.appSettings}
                device={device}
              />
            </div>
          </div>
        </div>

        {/* Right Sidebar — Properties */}
        <div className="w-64 bg-white border-l border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-bold text-gray-700">
              {selectedElement ? `${selectedElement.type.charAt(0).toUpperCase() + selectedElement.type.slice(1)} Properties` : 'App Settings'}
            </p>
          </div>
          <div className="flex-1 overflow-y-auto">
            <PropertiesPanel
              element={selectedElement}
              appSettings={state.appSettings}
              onUpdateElement={(id, props) => { pushHistory(state); dispatch({ type: 'UPDATE_ELEMENT', id, props }); }}
              onDeleteElement={(id) => { pushHistory(state); dispatch({ type: 'DELETE_ELEMENT', id }); }}
              onUpdateSettings={(settings) => dispatch({ type: 'UPDATE_APP_SETTINGS', settings })}
            />
          </div>
        </div>
      </div>

      {/* ── Plugin Manager ── */}
      {showPlugins && (
        <PluginManager onClose={() => setShowPlugins(false)} />
      )}

      {/* ── Preview Modal ── */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}>
          <div className="relative">
            <div className="absolute -top-14 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white rounded-xl p-1.5 shadow-xl border border-gray-200">
              <button onClick={() => setDevice('ios')}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${device === 'ios' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                <i className="ri-apple-fill"></i>iOS
              </button>
              <button onClick={() => setDevice('android')}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${device === 'android' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                <i className="ri-android-fill text-green-500"></i>Android
              </button>
            </div>
            <button onClick={() => setShowPreview(false)}
              className="absolute -top-14 right-0 w-9 h-9 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-lg cursor-pointer border border-gray-200">
              <i className="ri-close-line text-lg text-gray-700"></i>
            </button>
            <PhoneCanvas
              elements={activeScreen.elements}
              selectedId={null}
              onSelect={() => {}}
              onDrop={() => {}}
              onDragOver={() => {}}
              onReorder={() => {}}
              appSettings={state.appSettings}
              device={device}
              previewMode
            />
          </div>
        </div>
      )}

      {/* ── Publish Modal ── */}
      {showPublish && (
        <PublishModal
          appName={state.appName}
          onClose={() => setShowPublish(false)}
          onUpdateAppName={(name) => dispatch({ type: 'UPDATE_APP_NAME', name })}
        />
      )}
    </div>
  );
}
